import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export interface LeaderboardUserAvatarStyleOptions {
  /** Pixel size of the main avatar — drives the org-monkey image dimensions and circular crop. */
  size: number;
  /** When true (podium variants), badge override styles are returned at the larger scale. */
  isPodium: boolean;
}

/**
 * Size-variant styles for {@link LeaderboardUserAvatar}.
 *
 * Inline `width/height/borderRadius` previously lived on the org-monkey Image and on
 * the Google-account badge override. They are size-dependent (row vs podiumLarge vs
 * podiumSmall), but the size variants are a closed set — so we pre-build them here
 * instead of leaving inline objects in the JSX.
 */
export const getLeaderboardUserAvatarStyles = (
  _theme: Theme,
  options: LeaderboardUserAvatarStyleOptions,
) => {
  const { size, isPodium } = options;
  const badgeSize = isPodium ? Math.round(size * 0.32) : 15;
  const badgeFontSize = isPodium ? Math.max(10, Math.round(badgeSize * 0.42)) : 7;
  // The design's ~0.55 crest-to-avatar ratio, floored so a 34pt row avatar keeps a legible
  // crest (specs/rank-on-social-screens AC-10).
  const crestSize = Math.max(20, Math.round(size * 0.55));

  return StyleSheet.create({
    /** Circular crop for the org-monkey override avatar (row + podium variants). */
    orgMonkeyImage: {
      width: size,
      height: size,
      borderRadius: size / 2,
    },
    /**
     * Override applied on top of the shared `profileBadge` style on podium variants —
     * the chip scales with the avatar so a 100px podium avatar doesn't keep the 15px chip.
     */
    profileBadgePodiumOverride: {
      width: badgeSize,
      height: badgeSize,
      borderRadius: badgeSize / 2,
      right: -2,
      bottom: -2,
    },
    /** Initials font size when the badge is at the larger podium scale. */
    profileBadgeInitialsFontPodiumOverride: {
      fontSize: badgeFontSize,
    },
    /** Rank crest overhanging the avatar's bottom-right (design 1b). */
    rankCrest: {
      position: 'absolute',
      width: crestSize,
      height: crestSize,
      right: -Math.round(crestSize * 0.34),
      bottom: -Math.round(crestSize * 0.26),
      zIndex: 4,
    },
  });
};

/** Crest size for a given avatar size — mirrors the ratio used in the stylesheet above. */
export const getLeaderboardCrestSize = (size: number) => Math.max(20, Math.round(size * 0.55));
